'use client';

/**
 * **HTML 을 화면에 넣는 유일한 자리.** 남의 글을 그리는 모든 경로가 여기로 모인다.
 *
 * 통로를 하나로 좁히고 그 안에서 씻는다 — 이 파일 밖의 생짜 `dangerouslySetInnerHTML` 은
 * **게이트가 막는다**(`tools/html-injection-check.py` · 검사 ④-d).
 *
 * ## 🔑 세척이 **마지막 변환**이다
 *
 * 하이라이터가 `<mark>` 를 끼워 넣은 **최종 조합 문자열**을 받아 **그 자리에서** 씻는다.
 * 씻은 뒤에 무엇이든 덧붙이면 그 조합 단계가 새 구멍이 되므로, 씻기가 파이프라인의
 * **끝**이어야 한다.
 *
 * ## ⚠️ 자체 세척기를 짜지 않는다
 *
 * 「태그를 걷은 뒤 엔티티를 디코딩」하는 자체 구현은 **디코딩이 태그를 부활**시킨다(mXSS).
 * DOMPurify(Cure53 유지)는 그것을 설계상 막고 새 벡터도 라이브러리가 따라간다 —
 * 우리는 버전만 올린다.
 *
 * ## 🔑 서버에서는 씻지 않고 글자로 그린다
 *
 * Next 는 클라이언트 컴포넌트도 첫 HTML 을 서버에서 그린다. 서버에는 DOM 이 없어 DOMPurify 가 돌지 않고,
 * jsdom 은 운영 함수 런타임에서 불러오다 죽는다(그 경계 안의 화면 전체가 첫 HTML 에서 빈다). 그래서 서버와
 * 하이드레이션 첫 그림은 하이라이트 조각을 React 텍스트 노드로 그리고(`HighlightSegmentNodes` — 태그가 만들어질
 * 수 없다), 브라우저에서 DOMPurify 로 씻은 HTML 로 바꾼다. 브라우저에서 처음 그리는 자리는 바로 씻은 HTML 이다.
 * ⛔ 순수 `dompurify` 는 DOM 이 없으면 입력을 **씻지 않고 그대로** 돌려준다 — `sanitizeHighlightedHtml` 이 그 경우
 *   빈 문자열을 낸다(닫힌 쪽으로 실패).
 */

import DOMPurify from 'dompurify';
import { useSyncExternalStore } from 'react';

import { toHighlightSegments } from 'src/utils/text/highlight-segments';

import { HighlightSegmentNodes } from './highlight-segment-nodes';

/**
 * 허용 목록 — **본문 하이라이터가 «스스로» 넣는 것만.**
 *
 * `utils/text/text-highlight.ts` 가 내는 태그는 둘뿐이다:
 * ```
 *   <mark style="background-color: …; color: #000;">   검색어·토픽·클라 검색어
 *   <span style="color: …;">                            감성(긍정 파랑·부정 빨강)
 * ```
 * ⚠️ **여기에 태그를 더하려면 근거가 있어야 한다.** 「원문 서식을 살리자」는 기각된 안이다 —
 * 수집 본문에 `<br>`·`<p>`·`<a>`·`<img>` 는 사실상 오지 않고, 유일하게 오는
 * `<b>`(네이버 검색 강조)는 화면에 안 보인다. 지킬 서식이 없는데 목록을 넓히면
 * **지키는 것 없이 공격면만 는다.**
 */
const ALLOWED_TAGS = ['mark', 'span'];
const ALLOWED_ATTR = ['style'];

/**
 * 씻기 — 이 모듈 밖으로 새지 않게 한 곳에 둔다.
 * @returns 씻은 HTML. DOM 이 없는 곳(서버)에서는 씻을 수 없으므로 빈 문자열 — 부르기 전에 `useSanitizerReady` 를 본다
 */
export function sanitizeHighlightedHtml(html: string | null | undefined): string {
  if (typeof html !== 'string' || html === '' || !DOMPurify.isSupported) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // 태그를 지우되 **글자는 남긴다** — 사용자가 쓴 말이 사라지면 그것대로 사고다
    KEEP_CONTENT: true,
    // 문서 조각으로 다룬다(<html><body> 를 씌우지 않는다)
    WHOLE_DOCUMENT: false,
  });
}

/**
 * 이 자리에서 DOMPurify 로 씻을 수 있나 — 서버와 하이드레이션 첫 그림은 `false`(서버 HTML 과 같아야 한다), 그 뒤와
 * 브라우저에서 처음 그리는 자리는 `true`.
 */
export function useSanitizerReady(): boolean {
  return useSyncExternalStore(subscribeNothing, readSanitizerSupport, readServerSupport);
}

export type SafeHtmlProps = {
  /** 하이라이트까지 끝난 **최종** 문자열 */
  html: string | null | undefined;
  /** 겉을 감쌀 태그 — 기본 `span`(문장 안에 들어가는 자리가 많다) */
  as?: 'span' | 'div' | 'p';
  className?: string;
  style?: React.CSSProperties;
  title?: string;
};

export default function SafeHtml({
  html,
  as: Tag = 'span',
  className,
  style,
  title,
}: SafeHtmlProps) {
  const ready = useSanitizerReady();

  if (!ready) {
    return (
      <Tag className={className} style={style} title={title}>
        <HighlightSegmentNodes segments={toHighlightSegments(html)} />
      </Tag>
    );
  }

  const clean = sanitizeHighlightedHtml(html);
  return (
    <Tag
      className={className}
      style={style}
      title={title}
      // 🔑 리포에서 **유일하게 허용된** 주입 자리다. 바로 위에서 DOMPurify 를 지난 값만
      //    들어오고, 밖에서 생짜로 쓰는 것은 검사 ④-d(`tools/html-injection-check.py`)가 막는다.
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

// ---- private ------------------------------------------------------------

/** DOM 지원 여부는 바뀌지 않는다 — 구독할 것이 없다 */
function subscribeNothing(): () => void {
  return () => {};
}

function readSanitizerSupport(): boolean {
  return DOMPurify.isSupported;
}

function readServerSupport(): boolean {
  return false;
}
